// reportDataSlice.ts
import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import moment from 'moment-timezone';

type ReportDoc = Record<string, any>;

export type FourWeekBucketDTO = {
  chunkStart: string | Date;
  count: number;
  reports: ReportDoc[];
};

export type NormalizedBin = {
  startMs: number; // numeric key
  startISO: string; // ISO (no Date objects in state)
  endExclusiveISO: string; // ISO
  label: string; // "Jul 14 – Aug 10, 2025"
  count: number;
  reports: ReportDoc[]; // serializable
  byLayout: Record<string, ReportDoc[]>;
  isCurrent: boolean;
};

interface ReportDataState {
  // key = startMs as string to avoid Map
  reportData: Record<string, NormalizedBin>;
  // NEW: O(1) access to reports by _id
  indexByReportId: Record<string, ReportDoc>;
}

const initialState: ReportDataState = {
  reportData: {},
  indexByReportId: {},
};

// ---- helpers ----
function sanitizeReport(r: ReportDoc): ReportDoc {
  if (!r) return r;
  // normalize ids and dates to serializable primitives
  const _id = (r._id ?? r.id ?? '').toString();
  const reportLayoutId =
    r.reportLayoutId != null ? r.reportLayoutId.toString() : r.reportLayoutId;

  const submittedAt =
    r.submittedAt instanceof Date ? r.submittedAt.toISOString() : r.submittedAt;
  const createdAt =
    r.createdAt instanceof Date ? r.createdAt.toISOString() : r.createdAt;
  const updatedAt =
    r.updatedAt instanceof Date ? r.updatedAt.toISOString() : r.updatedAt;

  return { ...r, _id, reportLayoutId, submittedAt, createdAt, updatedAt };
}

function buildIndexByReportId(
  bins: FourWeekBucketDTO[]
): Record<string, ReportDoc> {
  const out: Record<string, ReportDoc> = {};
  for (const b of bins ?? []) {
    for (const r of b.reports ?? []) {
      const sr = sanitizeReport(r);
      if (sr._id) out[sr._id] = sr; // last write wins if dupes across bins
    }
  }
  return out;
}

function normalizeBins(
  bins: FourWeekBucketDTO[],
  timezone: string
): Record<string, NormalizedBin> {
  const now = moment.tz(timezone);
  const out: Record<string, NormalizedBin> = {};

  for (const b of bins) {
    const start = moment.tz(b.chunkStart, timezone);
    const endExclusive = start.clone().add(4, 'weeks');
    const key = String(start.valueOf());

    const byLayout: Record<string, ReportDoc[]> = {};
    const reports = (b.reports ?? []).map(sanitizeReport);

    for (const r of reports) {
      const lid = (r.reportLayoutId ?? 'unknown').toString();
      (byLayout[lid] ??= []).push(r);
    }

    out[key] = {
      startMs: Number(key),
      startISO: start.toISOString(),
      endExclusiveISO: endExclusive.toISOString(),
      label: `${start.format('MMM D')} – ${endExclusive.clone().subtract(1, 'day').format('MMM D, YYYY')}`,
      count: b.count ?? reports.length ?? 0,
      reports,
      byLayout,
      isCurrent: now.isSameOrAfter(start) && now.isBefore(endExclusive),
    };
  }

  return out;
}

// ---- slice ----
const reportDataSlice = createSlice({
  name: 'reportData',
  initialState,
  reducers: {
    // Build both the 4-week bins and the id index in one go
    setReportDataFromBins: (
      state,
      action: PayloadAction<{ bins: FourWeekBucketDTO[]; timezone: string }>
    ) => {
      const { bins, timezone } = action.payload;
      state.reportData = normalizeBins(bins, timezone);
      state.indexByReportId = buildIndexByReportId(bins);
    },
    clearReportData: (state) => {
      state.reportData = {};
      state.indexByReportId = {};
    },
    // Optional: upsert a single report into the index (e.g., after creating a new report)
    upsertReportIntoIndex: (state, action: PayloadAction<ReportDoc>) => {
      const sr = sanitizeReport(action.payload);
      if (sr?._id) state.indexByReportId[sr._id] = sr;
    },
  },
});

export const { setReportDataFromBins, clearReportData, upsertReportIntoIndex } =
  reportDataSlice.actions;
export default reportDataSlice.reducer;

// --- Selectors ---
export const selectBinRecord = (s: any) =>
  s.reportData.reportData as Record<string, NormalizedBin>;

export const selectBinByStartMs = (startMs: number) => (s: any) =>
  selectBinRecord(s)[String(startMs)];

export const selectBinsSorted = (s: any) =>
  Object.values(selectBinRecord(s)).sort((a, b) => b.startMs - a.startMs);

export const selectCurrentBin = (s: any) =>
  Object.values(selectBinRecord(s)).find((b) => b.isCurrent);

export const selectReportsForLayout = (layoutId: string) => (s: any) =>
  selectCurrentBin(s)?.byLayout?.[layoutId] ?? [];

// NEW: O(1) accessors
export const selectIndexByReportId = (s: any) =>
  (s.reportData.indexByReportId ?? {}) as Record<string, ReportDoc>;

export const selectReportById =
  (id: string) =>
  (s: any): ReportDoc | undefined =>
    selectIndexByReportId(s)[id];
